import { Eye, Loader2 } from 'lucide-react';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';

import { cn } from '@/lib/utils';

import {
  AgentChatContext,
  AgentChatProvider,
  useAgentChatContext,
} from './agent-chat-provider';
import { groupMessagesByContext } from './context-switch';
import { ContextSwitchMarker } from './context-switch-marker';
import { DecisionPanel } from './decision-panel';
import { MessageBubble } from './message-bubble';
import { PromptBar } from './prompt-bar';
import { PromptQueueStrip } from './prompt-queue-strip';
import type { AgentChatPanelProps } from './types';

const FOLLOW_THRESHOLD_PX = 80;

export function AgentChatPanel(props: AgentChatPanelProps) {
  const existingContext = useContext(AgentChatContext);
  const body = (
    <AgentChatPanelBody
      layout={props.layout}
      ground={props.ground ?? 'sidebar'}
      composerPlaceholder={props.composerPlaceholder}
      attachmentsEnabled={props.attachmentsEnabled ?? true}
      customInstructionsEnabled={props.customInstructionsEnabled ?? false}
      clarificationSettingsEnabled={props.clarificationSettingsEnabled ?? false}
      skillsEnabled={props.skillsEnabled ?? false}
      workflowCommands={props.workflowCommands ?? false}
      emptyState={props.emptyState}
      emptyStateActions={props.emptyStateActions}
      pendingFirstTurn={props.pendingFirstTurn ?? false}
    />
  );
  if (existingContext) return body;
  return (
    <AgentChatProvider
      agentId={props.agentId}
      conversationId={props.conversationId}
      onConversationChange={props.onConversationChange}
      surfaceContext={props.surfaceContext}
      openWorkflowId={props.openWorkflowId}
      modelOverride={props.modelOverride}
      onArtifact={props.onArtifact}
      onOpenArtifact={props.onOpenArtifact}
      onOpenWorkflow={props.onOpenWorkflow}
      onRunStarted={props.onRunStarted}
      onApplyMutations={props.onApplyMutations}
      applyMutationsDisabled={props.applyMutationsDisabled}
      applyMutationsDisabledReason={props.applyMutationsDisabledReason}
      onRunWorkflow={props.onRunWorkflow}
    >
      {body}
    </AgentChatProvider>
  );
}

interface AgentChatPanelBodyProps {
  layout: 'rail' | 'full';
  ground: NonNullable<AgentChatPanelProps['ground']>;
  composerPlaceholder?: string;
  attachmentsEnabled: boolean;
  customInstructionsEnabled: boolean;
  clarificationSettingsEnabled: boolean;
  skillsEnabled: boolean;
  workflowCommands: boolean;
  emptyState?: ReactNode;
  emptyStateActions?: ReactNode;
  pendingFirstTurn: boolean;
}

function AgentChatPanelBody({
  layout,
  ground,
  composerPlaceholder,
  attachmentsEnabled,
  customInstructionsEnabled,
  clarificationSettingsEnabled,
  skillsEnabled,
  workflowCommands,
  emptyState,
  emptyStateActions,
  pendingFirstTurn,
}: AgentChatPanelBodyProps) {
  const {
    agent,
    messages,
    isLoadingMessages,
    error,
    writeAccess,
    isSending,
    pendingDecision,
    answerDecision,
    answerDecisionWithText,
    dismissDecision,
    focusComposer,
  } = useAgentChatContext();
  const scrollAnchorRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  // Following every delta made a streaming reply impossible to scroll back.
  const followRef = useRef(true);
  // "Something else" hides DecisionPanel and PromptBar remounts in its
  // place, so the composer isn't there to focus yet in the same click
  // handler that closes the panel -- this arms a focus for once it is.
  const focusComposerOnMountRef = useRef(false);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    followRef.current = distanceFromBottom <= FOLLOW_THRESHOLD_PX;
  }, []);

  useEffect(() => {
    if (!followRef.current) return;
    scrollAnchorRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [messages]);

  const showLoading =
    (isLoadingMessages || pendingFirstTurn) && messages.length === 0;
  const showEmpty = !showLoading && messages.length === 0;

  // Rows grow after they land (a run card fills in, a document card loads,
  // a relayed question renders its countdown), which pushes the end back
  // out of view without a message change to scroll on. Following the
  // list's size keeps a reader at the bottom through that as well.
  useEffect(() => {
    const list = listRef.current;
    if (!list || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      if (!followRef.current) return;
      scrollAnchorRef.current?.scrollIntoView({ behavior: 'auto' });
    });
    observer.observe(list);
    return () => observer.disconnect();
  }, [showLoading, showEmpty]);

  // Bugbot: the panel does not remount on New Chat, so a reader who had
  // scrolled away would never start following the next reply.
  useEffect(() => {
    if (messages.length === 0) followRef.current = true;
  }, [messages.length]);

  useEffect(() => {
    if (!pendingDecision && focusComposerOnMountRef.current) {
      focusComposerOnMountRef.current = false;
      focusComposer();
    }
  }, [pendingDecision, focusComposer]);

  // Sectioned so each marker is sticky only for the stretch it opened --
  // the next one then pushes it out rather than overlapping it.
  const contextSections = useMemo(
    () => groupMessagesByContext(messages),
    [messages],
  );

  // A view-only share outranks a pending question: answering one posts a
  // message, which the server refuses for a viewer. While the role is still
  // unknown we show neither -- a composer painted here is one a viewer can
  // type into, and a notice painted here would flash on the owner's own
  // chats. Otherwise a pending question takes the composer's place: the
  // agent has ended its turn waiting on the answer, and a question the
  // user can type past is a question they will type past.
  // Stays null while writeAccess is 'unknown'.
  let composer: ReactNode = null;
  if (writeAccess === 'denied') {
    composer = (
      <div className='flex items-center gap-2 rounded-xl bg-accent px-3 py-2.5 text-sm text-muted-foreground'>
        <Eye className='size-4 shrink-0' />
        This chat was shared with you as view-only. Only its owner can send
        messages.
      </div>
    );
  } else if (writeAccess === 'allowed') {
    composer = pendingDecision ? (
      <DecisionPanel
        decision={pendingDecision}
        disabled={isSending}
        onPick={answerDecision}
        onFreeText={() => {
          focusComposerOnMountRef.current = true;
          answerDecisionWithText();
        }}
        onDismiss={dismissDecision}
      />
    ) : (
      <PromptBar
        placeholder={composerPlaceholder}
        attachmentsEnabled={attachmentsEnabled}
        customInstructionsEnabled={customInstructionsEnabled}
        clarificationSettingsEnabled={clarificationSettingsEnabled}
        skillsEnabled={skillsEnabled}
        workflowCommands={workflowCommands}
      />
    );
  }

  // Above the composer (and above a decision panel too -- queued prompts
  // are still there to see and manage while a question is up). Renders
  // nothing while the queue is empty.
  const queueStrip = writeAccess === 'allowed' ? <PromptQueueStrip /> : null;
  // The strip docks onto the composer's top edge, so they share one spacing
  // slot in each layout below.
  const dockedComposer = (
    <div className='flex flex-col gap-2'>
      {queueStrip}
      {composer}
    </div>
  );

  const errorBanner = error ? (
    <div className='rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive'>
      {error}
    </div>
  ) : null;

  if (showEmpty && emptyState) {
    return (
      <div className='subtle-scrollbar flex flex-1 flex-col min-h-0 min-w-0 overflow-y-auto'>
        <div className='flex flex-1 items-center justify-center p-6'>
          <div className='mx-auto flex w-full max-w-3xl flex-col gap-6'>
            {emptyState}
            {dockedComposer}
            {errorBanner}
            {emptyStateActions}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='flex flex-1 flex-col min-h-0 min-w-0'>
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        // Top padding lives on the message list, not here: a sticky context
        // marker pins below the scroll container's own padding, which would
        // leave a band of conversation scrolling above it.
        className='subtle-scrollbar flex flex-1 flex-col overflow-y-auto px-4 pb-6'
      >
        {showLoading && (
          <div className='flex items-center justify-center py-16 text-muted-foreground'>
            <Loader2 className='h-5 w-5 animate-spin mr-2' />
            {isLoadingMessages ? 'Loading conversation...' : 'Starting chat...'}
          </div>
        )}
        {showEmpty && emptyState === undefined && (
          <div className='flex flex-col items-center justify-center py-16 text-center max-w-md mx-auto'>
            <p className='text-sm font-medium'>
              Chat with {agent?.name ?? 'agent'}
            </p>
            <p className='text-xs text-muted-foreground mt-1'>
              Type a message below to start a new conversation.
            </p>
          </div>
        )}
        {!showLoading && !showEmpty && (
          <div
            ref={listRef}
            className={cn(
              // mt-auto keeps a short conversation next to the prompt bar.
              // pt-6 is the scroll container's top padding, moved in here so a
              // pinned marker sits flush against the top of the window.
              'mt-auto w-full space-y-6 pt-6',
              // The design insets the message window by 24px and the prompt
              // wrapper by 8px, so the conversation is slightly narrower.
              layout === 'full' && 'max-w-3xl mx-auto px-6',
            )}
          >
            {contextSections.map((section) => (
              <div key={section.key} className='space-y-6'>
                {section.context && (
                  <ContextSwitchMarker
                    context={section.context}
                    // Bleeds to the list's edge so nothing scrolls past its
                    // sides while pinned; the full layout's gutter is wider.
                    className={cn(
                      layout === 'full' && '-mx-6 px-6',
                      ground === 'background' && 'bg-background',
                    )}
                  />
                )}
                {section.messages.map((m) => (
                  <MessageBubble key={m._localId} message={m} />
                ))}
              </div>
            ))}
            <div ref={scrollAnchorRef} />
          </div>
        )}
      </div>
      <div
        className={cn(
          'shrink-0 space-y-2 px-2 pb-4 pt-2',
          layout === 'full' && 'mx-auto w-full max-w-3xl',
        )}
      >
        {errorBanner}
        {dockedComposer}
      </div>
    </div>
  );
}
